import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, BookOpen } from "lucide-react";
import api from "../lib/api";
import { PageHeader, Spinner } from "./common";
import { chipTint } from "../lib/chipTints";
import { placePhotoQuery, HoverPhotoPreview } from "./UnsplashGallery";

/* Shared country city-directory page (India, US, world directories...). Regions of pastel
   city chips, Unsplash hover previews, published-guide dots, old/new-name search.
   title/subtitle/accent/docTitle fall back to the API payload (world directories). */
export default function CityDirectory({ id, title, subtitle, endpoint, country,
  searchPlaceholder, accent, docTitle }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (docTitle) document.title = docTitle;
    api.get(endpoint).then((r) => {
      setData(r.data);
      if (!docTitle && r.data?.title) document.title = `${r.data.title} | TravelerPulse`;
    }).catch(() => setData({ regions: [] }));
  }, [endpoint, docTitle]);

  const regions = useMemo(() => {
    if (!data) return [];
    const needle = q.trim().toLowerCase();
    if (!needle) return data.regions;
    return data.regions
      .map((r) => ({ ...r, items: r.items.filter((i) =>
        i.label.toLowerCase().includes(needle) || i.state.toLowerCase().includes(needle)) }))
      .filter((r) => r.items.length > 0);
  }, [data, q]);

  const heading = title || data?.title || "City Guides";
  const sub = subtitle || data?.subtitle || "";
  const accentColor = accent || data?.accent || "#ff9933";

  return (
    <div data-testid={`${id}-page`}>
      <PageHeader eyebrow="TravelerPulse · Geographic Database" title={heading} subtitle={sub} />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex flex-wrap items-center gap-4 mb-8">
          <div className="relative flex-1 min-w-[240px] max-w-xl">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${id}-search`}
              placeholder={searchPlaceholder}
              className="w-full border border-zinc-300 bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-blue-600" />
          </div>
          {data && (
            <div className="text-sm text-muted-foreground" data-testid={`${id}-count`}>
              <b>{data.count}</b> places · <b>{data.guides_ready}</b> guides ready
              <span className="inline-flex items-center gap-1 ml-3 text-xs">
                <span className="w-2 h-2 rounded-full bg-emerald-500 inline-block" /> guide published
              </span>
            </div>
          )}
        </div>

        {!data ? <Spinner /> : regions.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid={`${id}-empty`}>No places match "{q}".</p>
        ) : regions.map((r) => (
          <section key={r.name} className="mb-10"
            data-testid={`${id}-region-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <h2 className="font-heading font-900 text-xl border-b-2 pb-2 mb-4" style={{ borderColor: accentColor }}>
              {r.name} <span className="text-sm font-600 text-muted-foreground">· {r.items.length} places</span>
            </h2>
            <div className="flex flex-wrap gap-1.5">
              {r.items.map((c, i) => (
                <HoverPhotoPreview key={c.slug} query={placePhotoQuery(c.label.split(" (")[0], country || c.state)}
                  name={c.label.split(" (")[0]} sub={c.state}
                  testId={`${id}-preview-${c.slug.split("/").pop()}`}>
                  <button onClick={() => nav(`/explore/${c.slug}`)}
                    data-testid={`${id}-city-${c.slug.split("/").pop()}`}
                    title={`${c.state}${c.has_guide ? " · guide published" : " · guide generates on first visit"}`}
                    style={{ background: chipTint(i) }}
                    className="px-2.5 py-1.5 border border-zinc-200 text-xs font-700 text-zinc-800 hover:border-[#1a73e8] hover:text-[#1a73e8] transition-colors">
                    {c.label}
                    {c.has_guide && <span className="ml-1.5 w-2 h-2 rounded-full bg-emerald-500 inline-block align-middle" />}
                  </button>
                </HoverPhotoPreview>
              ))}
            </div>
          </section>
        ))}

        <div className="mt-4 border border-zinc-200 bg-zinc-50 p-4 text-xs text-muted-foreground flex items-start gap-2">
          <BookOpen className="w-4 h-4 shrink-0 mt-0.5" />
          <p>
            Every place links to its AI travel guide (with Overview, Getting There, Things to See & Do,
            Food & Drink, Safety and more) — places without a green dot generate their guide on first
            visit (takes about a minute). Photo previews courtesy of the photographers on{" "}
            <a href="https://unsplash.com/?utm_source=travelerpulse&utm_medium=referral" target="_blank"
              rel="noreferrer" className="underline">Unsplash</a>.
          </p>
        </div>
      </div>
    </div>
  );
}
